<script setup>
import Modal from './Modal.vue';
import AppButton from './AppButton.vue';
import { toasts, confirmState, settleConfirm, dismissToast } from '@/composables/useDialogs';

function submit() {
  const action = confirmState.actions.find((a) => a.submit) || confirmState.actions[confirmState.actions.length - 1];
  if (action) settleConfirm(action.value);
}

function runAction(t) {
  dismissToast(t.id);
  t.action?.run?.();
}
</script>

<template>
  <Modal :open="confirmState.open" :title="confirmState.title" :width="confirmState.details?.length ? 520 : 440" @close="settleConfirm(null)">
    <p v-if="confirmState.message" class="message">{{ confirmState.message }}</p>
    <ul v-if="confirmState.details?.length" class="details">
      <li v-for="(line, i) in confirmState.details" :key="i">{{ line }}</li>
    </ul>
    <form v-if="confirmState.input" class="prompt" @submit.prevent="submit">
      <label class="field">
        <span v-if="confirmState.input.label" class="field-label">{{ confirmState.input.label }}</span>
        <input v-model="confirmState.input.value" class="input" type="text" :placeholder="confirmState.input.placeholder" autofocus />
      </label>
    </form>
    <template #footer>
      <div class="actions">
        <AppButton
          v-for="(action, i) in confirmState.actions"
          :key="action.label"
          :variant="action.variant || 'secondary'"
          :autofocus="(!confirmState.input && i === confirmState.actions.length - 1) || undefined"
          @click="settleConfirm(action.value)"
        >
          {{ action.label }}
        </AppButton>
      </div>
    </template>
  </Modal>

  <Teleport to="body">
    <div class="toasts" aria-live="polite">
      <TransitionGroup name="toast">
        <div v-for="t in toasts" :key="t.id" class="toast" :class="`toast--${t.tone}`">
          {{ t.message }}
          <button v-if="t.action" type="button" class="toast-action" @click="runAction(t)">{{ t.action.label }}</button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.message {
  color: var(--text-2);
  line-height: 1.6;
  white-space: pre-line;
}

.details {
  margin: 12px 0 0;
  padding: 12px 14px 12px 30px;
  max-height: 260px;
  overflow: auto;
  border-radius: var(--radius-s);
  background: var(--bg);
  color: var(--text);
  font-size: 13px;
  line-height: 1.6;
}

.prompt {
  margin-top: 12px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.toasts {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 10px;
  background: var(--ink);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: var(--shadow-pop);
  pointer-events: auto;
}

.toast-action {
  padding: 2px 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: transparent;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

.toast--error {
  background: var(--danger);
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
